export function Privacy() {
  return (
    <div className="max-w-3xl mx-auto py-8 px-4">
      <h1 className="text-3xl font-bold text-stone-900 dark:text-white mb-2">Privacy Policy</h1>
      <p className="text-sm text-stone-500 dark:text-zinc-500 mb-8">Last updated: September 8, 2026</p>

      <div className="prose prose-stone dark:prose-invert prose-sm max-w-none space-y-6">
        <section>
          <h2 className="text-lg font-semibold text-stone-900 dark:text-white">1. Information We Collect</h2>
          <p className="text-stone-600 dark:text-zinc-400 leading-relaxed">
            When you use SocialCast, we collect information necessary to provide our social media management services:
          </p>
          <ul className="list-disc pl-5 text-stone-600 dark:text-zinc-400 space-y-1">
            <li><strong>Account information:</strong> Email address, name, and password when you create an account.</li>
            <li><strong>Social media data:</strong> When you connect social media accounts (Twitter/X, Instagram, Facebook, TikTok, YouTube, LinkedIn, Reddit, Pinterest, Telegram), we access and store OAuth tokens to post on your behalf. We access your profile information, post content, and engagement metrics as authorized by each platform.</li>
            <li><strong>Content you create:</strong> Posts, media files, captions, and scheduling data you create through our platform.</li>
            <li><strong>Usage data:</strong> How you interact with our platform, including pages visited and features used.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-stone-900 dark:text-white">2. How We Use Your Information</h2>
          <ul className="list-disc pl-5 text-stone-600 dark:text-zinc-400 space-y-1">
            <li>To publish and schedule posts to your connected social media accounts.</li>
            <li>To display analytics and engagement metrics from your connected accounts.</li>
            <li>To manage your social media inbox (comments, replies, messages).</li>
            <li>To store and manage media files you upload for posting.</li>
            <li>To authenticate you and maintain your session.</li>
            <li>To improve our platform and develop new features.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-stone-900 dark:text-white">3. Social Media Platform Data</h2>
          <p className="text-stone-600 dark:text-zinc-400 leading-relaxed">
            We connect to third-party social media platforms via their official APIs using OAuth 2.0. We only request permissions necessary to provide our services. You can revoke access at any time by disconnecting your account in SocialCast or through the respective platform's settings.
          </p>
          <p className="text-stone-600 dark:text-zinc-400 leading-relaxed mt-2">
            We do not sell, share, or use your social media data for advertising purposes. Your content remains yours — we only act as a tool to help you manage and publish it.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-stone-900 dark:text-white">4. Data Storage and Security</h2>
          <p className="text-stone-600 dark:text-zinc-400 leading-relaxed">
            Your data is stored on secure servers. OAuth tokens and credentials are encrypted at rest using AES-256-GCM encryption. We implement industry-standard security measures to protect your data from unauthorized access, alteration, or destruction.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-stone-900 dark:text-white">5. Data Retention</h2>
          <p className="text-stone-600 dark:text-zinc-400 leading-relaxed">
            We retain your data for as long as your account is active. When you delete your account or disconnect a social media account, we delete the associated tokens and data within 30 days. Post content and analytics data may be retained in anonymized form for service improvement.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-stone-900 dark:text-white">6. Third-Party Services</h2>
          <p className="text-stone-600 dark:text-zinc-400 leading-relaxed">
            We integrate with the following platforms and their respective privacy policies apply to data on their services: Meta (Facebook, Instagram), X (Twitter), TikTok, YouTube (Google), LinkedIn, Reddit, Pinterest, and Telegram. We also use AI services (Anthropic Claude) for optional content generation features — your prompts are processed but not stored by the AI provider for training.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-stone-900 dark:text-white">7. Your Rights</h2>
          <ul className="list-disc pl-5 text-stone-600 dark:text-zinc-400 space-y-1">
            <li><strong>Access:</strong> You can request a copy of your data at any time.</li>
            <li><strong>Deletion:</strong> You can delete your account and all associated data.</li>
            <li><strong>Portability:</strong> You can export your post history and analytics data.</li>
            <li><strong>Revocation:</strong> You can disconnect any social media account at any time.</li>
            <li><strong>Correction:</strong> You can update your account information at any time.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-stone-900 dark:text-white">8. Cookies</h2>
          <p className="text-stone-600 dark:text-zinc-400 leading-relaxed">
            We use essential cookies to maintain your login session and project selection. Apteva analytics measures site visits and signup events. With your permission, the Meta Pixel measures page views and completed registrations for advertising attribution. Meta may receive browser and device information, the page URL, cookie identifiers, and your IP address. We do not include your email address, password, social account tokens, or post content in these advertising events.
          </p>
          <p className="text-stone-600 dark:text-zinc-400 leading-relaxed mt-2">
            Meta tracking is not loaded before you allow advertising cookies. You can decline or change your choice using Advertising cookie settings at the bottom of the site or app. Your choice is remembered across SocialCast subdomains for up to 180 days. We also respect Global Privacy Control and Do Not Track signals by not loading Meta tracking. See <a href="https://www.facebook.com/privacy/policy/" className="underline">Meta's Privacy Policy</a> for its processing practices.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-stone-900 dark:text-white">9. Changes to This Policy</h2>
          <p className="text-stone-600 dark:text-zinc-400 leading-relaxed">
            We may update this privacy policy from time to time. We will notify you of any material changes by posting the new policy on this page and updating the "last updated" date.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-stone-900 dark:text-white">10. Contact</h2>
          <p className="text-stone-600 dark:text-zinc-400 leading-relaxed">
            If you have any questions about this privacy policy, please contact us at <a href="mailto:privacy@socialcast.dev" className="text-orange-600 dark:text-orange-400 hover:underline">privacy@socialcast.dev</a>.
          </p>
        </section>
      </div>
    </div>
  );
}
